/**
 * Org-shaped payloads: repositories, teams, developers and the subscription.
 *
 * These are what unblock the rest of the demo rather than what the visitor
 * came to see. Two in particular are load-bearing:
 *
 * - `/repositories` and `/teams` populate the Scope dropdown. The filter bar
 *   self-fetches through `useRepositories`/`useTeams`, so without these the
 *   dropdown renders empty and the demo looks like a product with no repos -
 *   which is exactly what anonymous visitors saw before this work.
 * - `/subscriptions` decides feature gating. An anonymous visitor has no
 *   subscription, so premium pages would render an upgrade wall instead of the
 *   features we are trying to demonstrate.
 */

import type { Repository, Developer } from '@/types/core';
import type { Team, TeamListResponse } from '@/types/teams';
import type { SubscriptionInfo } from '@/types/subscription';
import type { DataCoverage } from '@/lib/api/dataCoverage';
import type { OrganizationMember } from '@/types/org';
import { PREMIUM_FEATURES } from '@/types/subscription';
import { DEMO_ORG_ID, DEMO_HUMANS, DEMO_REPOS, DEMO_TEAMS, demoDevelopers, findDemoTeam } from '../fixture';

/** Repositories in scope, optionally narrowed to a team. */
export function buildDemoRepositories(teamId?: string | null): Repository[] {
  const team = findDemoTeam(teamId);
  const repos = team
    ? DEMO_REPOS.filter((r) => team.repoIds.includes(r.repository.id))
    : DEMO_REPOS;
  return repos.map((r) => r.repository);
}

/** Curated team colours, matching the palette real teams are assigned from. */
const DEMO_TEAM_COLORS = ['#3B82F6', '#A855F7', '#06B6D4'];

export function buildDemoTeams(): TeamListResponse {
  const teams: Team[] = DEMO_TEAMS.map((team, index) => ({
    id: team.id,
    organization_id: DEMO_ORG_ID,
    name: team.name,
    slug: team.slug,
    color: DEMO_TEAM_COLORS[index % DEMO_TEAM_COLORS.length],
    source: 'manual',
    linear_team_id: null,
    // Confirmed, not draft: a draft team is filtered out of the Scope
    // dropdown, which is the one place these need to appear.
    confirmed_at: '2025-03-11T00:00:00Z',
    member_ids: team.memberIds,
    created_at: '2025-03-11T00:00:00Z',
    updated_at: '2026-09-01T00:00:00Z',
  }));

  return { teams, total: teams.length };
}

export function buildDemoDevelopers(excludeBots: boolean): Developer[] {
  return demoDevelopers(excludeBots).map((dev) => ({
    id: dev.id,
    organization_id: DEMO_ORG_ID,
    external_id: `demo-${dev.id}`,
    external_login: dev.login,
    name: dev.name,
    last_active_at: '2026-09-01T00:00:00Z',
    created_at: '2024-02-05T00:00:00Z',
    updated_at: '2026-09-01T00:00:00Z',
  }));
}

/**
 * How much history the fictional org holds.
 *
 * Drives the "we only have N days of data" caveats. The demo claims two years
 * of fully-synced history with nothing in progress, so those caveats stay
 * silent - a banner apologising for incomplete data is the wrong first
 * impression, and it would be untrue of a fixture that can answer any window.
 */
export function buildDemoDataCoverage(): DataCoverage {
  return {
    data_from: '2024-09-01',
    data_to: '2026-09-01',
    available_days: 730,
    history_days: 730,
    sync_in_progress: false,
    repos_pending_initial_sync: 0,
    repos_deepening: 0,
    history_ratchet_active: false,
    repos_total: DEMO_REPOS.length,
  };
}

/**
 * The visitor's membership of the fictional org.
 *
 * `usePermissions` calls this from several components, so leaving it uncovered
 * meant an anonymous /demo visitor issued six authenticated requests carrying a
 * fictional org id to the real backend on every page load. Role is 'viewer',
 * the least privileged: the demo is for looking at, and a viewer cannot be
 * offered destructive or billing actions it would be dishonest to render.
 */
export function buildDemoMembership(): OrganizationMember {
  return {
    id: 'demo-membership',
    user_id: 'demo-visitor',
    organization_id: DEMO_ORG_ID,
    role: 'viewer',
    name: 'Demo visitor',
    created_at: '2026-09-01T00:00:00Z',
  };
}

/**
 * A synthetic subscription that unlocks the product.
 *
 * Deliberately a paid tier, not a trial: a trial subscription would render the
 * trial countdown banner, and "your trial ends in N days" is nonsense on a page
 * the visitor reached without an account. The tier only affects what the demo
 * renders - it grants nothing, because every request is answered locally.
 */
export function buildDemoSubscription(): SubscriptionInfo {
  return {
    id: 'demo-subscription',
    organization_id: DEMO_ORG_ID,
    tier: 'business',
    effective_tier: 'business',
    status: 'active',
    is_trial: false,
    is_trial_active: false,
    // 'none', not 'active': a trial state renders the countdown banner, and
    // "your trial ends in N days" is nonsense to a visitor with no account.
    trial_state: 'none',
    trial_days_remaining: null,
    trial_ends_at: null,
    limits: {
      max_developers: 150,
      current_developers: DEMO_HUMANS.length,
      current_mau: DEMO_HUMANS.length,
      all_time_developers: DEMO_HUMANS.length,
      active_window_days: 30,
      max_organizations: null,
      max_alert_rules: null,
      retention_days: 365,
    },
    features: [...PREMIUM_FEATURES],
    premium_features_locked: [],
    stripe_customer_id: null,
    current_period_end: null,
    cancel_at_period_end: false,
    is_paused: false,
    paused_at: null,
    pause_ends_at: null,
    created_at: '2024-02-05T00:00:00Z',
    updated_at: '2026-09-01T00:00:00Z',
  };
}
